import type { ApiConfig } from '@api/types';
import { typedMethodPost, typedMethodGet, typedMethodPostWithRequiredParams } from '@api/utils';
import type {
  DocumentSearchResponseDto,
  DocumentDto,
  UpdateDocumentMetadataRequestDto,
  RotateImageRequestDto,
  CropImageRequestDto,
  DocumentUploadRequestDto,
  DocumentUploadResponseDto,
  ProcessDocumentRequestDto,
  RemoveImageRequestDto,
  RemoveDocumentRequestDto,
  SearchImagesRequestDto,
  SearchImagesResponseDto,
  FilterDocumentsResponseDto,
  DocumentCategoryDto,
  DocumentUploadRequest2Dto,
  DocumentUploadResponse2Dto,
} from '../../generated/models';
import type { GetDocumentByIdParams, GetByHashParams, GetDocumentParams, SasResponse as SasResponseLocal } from './types';

// Domain types
import type {
  Document,
  DocumentSearchResponse,
  DocumentUploadResponse,
  SearchImagesResponse,
  FilterDocumentsResponse,
  DocumentCategory,
  SasResponse,
  DocumentUploadResponse2,
} from '../../domain/types';

// Mappers
import {
  toDocumentArray,
  toDocumentSearchResponse,
  toDocumentUploadResponse,
  toSearchImagesResponse,
  toFilterDocumentsResponse,
  toDocumentCategoryArray,
  toSasResponse,
  toDocumentUploadResponse2,
} from '../../domain/mappers';

export default ({ baseURL, baseURLoData }: ApiConfig) => ({
  retrieveReferencedDocuments: typedMethodPost<unknown, FilterDocumentsResponseDto, FilterDocumentsResponse>(
    (data) =>
      fetch(`${baseURL}/v2/documents/retrieveReferencedDocuments`, {
        method: 'POST',
        ...data,
      }), 
    toFilterDocumentsResponse
  ),
  searchDocuments: typedMethodPost<unknown, DocumentSearchResponseDto, DocumentSearchResponse>(
    (data) =>
      fetch(`${baseURL}/v2/search/documents`, {
        method: 'POST',
        ...data,
      }),
    toDocumentSearchResponse
  ),
  getDocumentById: typedMethodGet<GetDocumentByIdParams, DocumentDto[], Document[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/document?filter=documentId eq ${data.params.documentId}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toDocumentArray
  ),
  updateImageMetadata: typedMethodPost<UpdateDocumentMetadataRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/documents/updateDocumentMetadata`, {
        method: 'POST',
        ...data,
      })
  ),
  rotate: typedMethodPost<RotateImageRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/images/rotateImage`, {
        method: 'POST',
        ...data,
      })
  ),
  cropImage: typedMethodPost<CropImageRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/images/cropImage`, {
        method: 'POST',
        ...data,
      })
  ),
  requestForDocumentUpload: typedMethodPost<DocumentUploadRequestDto, DocumentUploadResponseDto, DocumentUploadResponse>(
    (data) =>
      fetch(`${baseURL}/v2/feed/requestForDocumentUpload`, {
        method: 'POST',
        ...data,
      }),
    toDocumentUploadResponse
  ),
  processDocument: typedMethodPost<ProcessDocumentRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/feed/processDocument`, {
        method: 'POST',
        ...data,
      })
  ),
  removeImage: typedMethodPost<RemoveImageRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/images/removeImage`, {
        method: 'POST',
        ...data,
      })
  ),
  removeDocument: typedMethodPost<RemoveDocumentRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/documents/remove`, {
        method: 'POST',
        ...data,
      })
  ),
  getUserProfileImages: typedMethodPost<SearchImagesRequestDto, SearchImagesResponseDto, SearchImagesResponse>(
    (data) =>
      fetch(`${baseURL}/v1/images/searchImages`, {
        method: 'POST',
        ...data,
      }),
    toSearchImagesResponse
  ),
  getByHash: typedMethodPostWithRequiredParams<GetByHashParams, FilterDocumentsResponseDto, FilterDocumentsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/documents/filter`, {
        method: 'POST',
        ...data,
        body: JSON.stringify({
          Filters: data.params,
          pagination: {
            pageNumber: 1,
            pageSize: 10,
            includeCount: true,
          },
        }),
      }),
    toFilterDocumentsResponse
  ),
  getSas: typedMethodGet<void, SasResponseLocal, SasResponse>(
    (data) =>
      fetch(`${baseURL}/v1/images/sas`, {
        method: 'GET',
        headers: data.headers,
      }),
    toSasResponse
  ),
  getDocumentCategories: typedMethodGet<void, DocumentCategoryDto[], DocumentCategory[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/v2/DocumentCategories`, {
        method: 'GET',
        headers: data.headers,
      }),
    toDocumentCategoryArray
  ),
  getDocument: typedMethodGet<GetDocumentParams, DocumentDto[], Document[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/Document?${data.params.filters}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toDocumentArray
  ),
  requestForDocumentUploadV2: typedMethodPost<DocumentUploadRequest2Dto, DocumentUploadResponse2Dto, DocumentUploadResponse2>(
    (data) =>
      fetch(`${baseURL}/v2/feed/requestForDocumentUpload`, {
        method: 'POST',
        ...data,
      }),
    toDocumentUploadResponse2
  ),
});

// Re-export domain types for consumers
export type {
  Document,
  DocumentSearchResponse,
  DocumentUploadResponse,
  SearchImagesResponse,
  FilterDocumentsResponse,
  DocumentCategory,
  SasResponse,
};
export type { GetDocumentByIdParams, GetByHashParams, GetDocumentParams } from './types';
